import React, { useState, useRef } from 'react';
import { MdClose, MdCloudUpload, MdCheck, MdWarning, MdAutoAwesome, MdSearch, MdDelete } from 'react-icons/md';
import AutoCompleteField from '@components/AutoCompleteV2';
import aiService from '@services/ai';
import providersService from '@services/providers';
import productsService from '@services/products';

const PDFUploadModal = ({ isOpen, onClose, onDataParsed, profile, onProviderCreated, products, postos, providers }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  const [error, setError] = useState(null);
  const [supplierNotFound, setSupplierNotFound] = useState(false);
  const [supplierData, setSupplierData] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [productsNotFound, setProductsNotFound] = useState([]);
  const [productMappings, setProductMappings] = useState({});
  const [manuallyConfirmedProducts, setManuallyConfirmedProducts] = useState(new Set());
  const [isCreatingSupplier, setIsCreatingSupplier] = useState(false);
  const [selectedPosto, setSelectedPosto] = useState(null);
  const [isCreatingCodes, setIsCreatingCodes] = useState(false);
  const [codesCreationResult, setCodesCreationResult] = useState(null);
  const fileInputRef = useRef(null);


  const handleFileUpload = async (file) => {
    if (!file) return;

    setIsUploading(true);
    setError(null);

    const formData = new FormData();
    formData.append('pdf_file', file);
    formData.append('idempresa', profile.idEmpresa);
    formData.append('format', 'orders');

    try {
      const data = await aiService.parseInvoice(formData);

      if (data.success) {
        setParsedData(data.data);
        await processParsedData(data.data);
      } else {
        setError(data.error || 'Erro ao processar o PDF');
      }
    } catch (err) {
      setError('Erro de conexão: ' + err.message);
    } finally {
      setIsUploading(false);
    }
  };

  const processParsedData = async (data) => {
    
    // Verificar se fornecedor existe
    if (data.idprovider === null || data.idprovider === undefined) {
      setSupplierNotFound(true);
      setSupplierData(data._metadata?.supplier);
    }

    // Verificar produtos que precisam de associação baseado no match_confidence
    const productsNeedingAssociation = data.lines?.filter(line => {
      // Se não tem idproduct, precisa de associação
      if (!line.idproduct) return true;
      
      // Se tem match_confidence "match", significa que não existe na tabela products_providers_codes
      // e precisa de associação manual
      if (line.match_confidence === "match") return true;
      
      // Se tem match_confidence "exact", já existe na tabela products_providers_codes
      // e não precisa de associação manual
      if (line.match_confidence === "exact") return false;
      
      // Caso não tenha match_confidence definido, assumir que precisa de associação
      return true;
    }) || [];
    
    setProductsNotFound(productsNeedingAssociation);
    
    // Inicializar mapeamentos com os produtos já confirmados (match_confidence: "exact")
    // e também com os produtos detectados pela IA (match_confidence: "match")
    const initialMappings = {};
    if (data.lines) {
      data.lines.forEach((line, index) => {
        if (line.idproduct && line.match_confidence === "exact") {
          // Produto já existe na tabela products_providers_codes
          const foundProduct = products.find(p => p.idproduct === line.idproduct);
          if (foundProduct) {
            initialMappings[index] = foundProduct;
          }
        } else if (line.idproduct && line.match_confidence === "match") {
          // Produto detectado pela IA - selecionar automaticamente na dropdown
          const foundProduct = products.find(p => p.idproduct === line.idproduct);
          if (foundProduct) {
            initialMappings[index] = foundProduct;
          }
        }
      });
    }
    setProductMappings(initialMappings);
  };

  const handleUseSelectedProvider = () => {
    if (!selectedProvider) return;
    
    setSupplierNotFound(false);
    // Atualizar o ID do fornecedor nos dados
    setParsedData(prev => ({
      ...prev,
      idprovider: selectedProvider.value || selectedProvider.idprovider,
      _metadata: {
        ...prev._metadata,
        supplier: {
          name: selectedProvider.name,
          vat_number: selectedProvider.nif,
          address: selectedProvider.address || '',
        }
      }
    }));
  };

  const handleCreateSupplier = async () => {
    if (!supplierData) return;

    setIsCreatingSupplier(true);
    setError(null);

    try {
      const result = await providersService.set({
        idempresa: profile.idEmpresa,
        name: supplierData.name,
        nif: supplierData.vat_number,
        address: supplierData.address,
        email: '',
        phone: '',
      });
      
      // A API retorna o ID do fornecedor diretamente ou um erro
      // Se a resposta for um número, é o ID do fornecedor criado
      // Se for um objeto, pode ter diferentes formatos
      let idprovider = null;
      
      if (typeof result === 'number') {
        idprovider = result;
      } else if (result.idprovider) {
        idprovider = result.idprovider;
      } else if (result.data?.idprovider) {
        idprovider = result.data.idprovider;
      } else if (!result.error) {
        // Se a resposta foi bem-sucedida mas não temos ID, tentar buscar o fornecedor pelo NIF
        const providers = await providersService.get(profile.idEmpresa);
        
        if (providers && Array.isArray(providers)) {
          const createdProvider = providers.find(p => p.nif === supplierData.vat_number);
          if (createdProvider) {
            idprovider = createdProvider.idprovider;
          }
        }
      }
      
      if (idprovider) {
        
        setSupplierNotFound(false);
        // Atualizar o ID do fornecedor nos dados
        setParsedData(prev => ({
          ...prev,
          idprovider: idprovider
        }));
        
        // Notificar o componente pai e AGUARDAR a recarga da lista de fornecedores
        if (onProviderCreated) {
          await onProviderCreated(idprovider);
        }
      } else {
        setError('Erro ao criar fornecedor: ' + (result.error || 'Não foi possível obter o ID do fornecedor'));
      }
    } catch (err) {
      setError('Erro ao criar fornecedor: ' + err.message);
    } finally {
      setIsCreatingSupplier(false);
    }
  };

  const handleProductMapping = (lineIndex, selectedProduct) => {
    setProductMappings(prev => ({
      ...prev,
      [lineIndex]: selectedProduct
    }));
    
    // Marcar como manualmente confirmado quando o utilizador interage com a dropdown
    setManuallyConfirmedProducts(prev => {
      const newSet = new Set(prev);
      newSet.add(lineIndex);
      return newSet;
    });
  };

  const handleDeleteLine = (lineToDelete) => {
    const lineIndexToDelete = parsedData.lines.findIndex(l => l === lineToDelete);
    
    // Remover a linha dos parsedData e recalcular totais
    setParsedData(prev => {
      const newLines = prev.lines.filter(line => line !== lineToDelete);
      
      // Recalcular subtotal e total
      const newSubtotal = newLines.reduce((sum, line) => {
        const lineTotal = (line.quantity || 0) * (line.costprice || 0);
        return sum + lineTotal;
      }, 0);
      
      // Recalcular IVA
      const newVat = newLines.reduce((sum, line) => {
        const lineTotal = (line.quantity || 0) * (line.costprice || 0);
        const lineVat = lineTotal * ((line.iva || 0) / 100);
        return sum + lineVat;
      }, 0);
      
      const newTotal = newSubtotal + newVat;
      
      return {
        ...prev,
        lines: newLines,
        _metadata: {
          ...prev._metadata,
          totals: {
            ...prev._metadata?.totals,
            subtotal: parseFloat(newSubtotal.toFixed(2)),
            vat: parseFloat(newVat.toFixed(2)),
            total: parseFloat(newTotal.toFixed(2))
          }
        }
      };
    });

    // Remover dos produtos não encontrados se estiver lá
    setProductsNotFound(prev => prev.filter(line => line !== lineToDelete));

    // Recalcular mapeamentos: remover o índice deletado e ajustar os índices seguintes
    if (lineIndexToDelete !== -1) {
      setProductMappings(prev => {
        const newMappings = {};
        Object.keys(prev).forEach(key => {
          const index = parseInt(key);
          if (index < lineIndexToDelete) {
            // Índices antes da linha deletada mantêm-se iguais
            newMappings[index] = prev[index];
          } else if (index > lineIndexToDelete) {
            // Índices depois da linha deletada são decrementados
            newMappings[index - 1] = prev[index];
          }
          // Se index === lineIndexToDelete, não adicionamos (linha deletada)
        });
        return newMappings;
      });
      
      // Recalcular produtos manualmente confirmados: remover o índice deletado e ajustar os índices seguintes
      setManuallyConfirmedProducts(prev => {
        const newSet = new Set();
        prev.forEach(index => {
          if (index < lineIndexToDelete) {
            // Índices antes da linha deletada mantêm-se iguais
            newSet.add(index);
          } else if (index > lineIndexToDelete) {
            // Índices depois da linha deletada são decrementados
            newSet.add(index - 1);
          }
          // Se index === lineIndexToDelete, não adicionamos (linha deletada)
        });
        return newSet;
      });
    }
  };

  // Verificar se todos os produtos não encontrados foram mapeados
  const areAllProductsMapped = () => {
    if (productsNotFound.length === 0) return true;
    
    // Verificar se cada produto não encontrado tem um mapeamento
    return productsNotFound.every(product => {
      const actualLineIndex = parsedData.lines.findIndex(l => l === product);
      return productMappings[actualLineIndex] !== undefined && productMappings[actualLineIndex] !== null;
    });
  };

  const handleConfirmData = async () => {
    // Preparar informações sobre associações de produtos para o backend
    const providerAssociations = [];
    const combinations = []; // Para a API bulk
    
    // Aplicar os mapeamentos de produtos e posto antes de enviar
    const updatedData = {
      ...parsedData,
      lines: parsedData.lines.map((line, index) => {
        // Usar o índice atual da linha em parsedData.lines para buscar o mapeamento
        const mappedProduct = productMappings[index];
        const finalProduct = mappedProduct || line.idproduct;
        const finalProductId = mappedProduct?.idproduct || line.idproduct;
        
        // Se há um produto final (detectado ou mapeado) e temos fornecedor e código/barcode
        if (finalProduct && parsedData.idprovider && line.barcode) {
          const isAiDetected = line.idproduct && !line.provider_code_confirmed;
          const wasManuallyMapped = mappedProduct && mappedProduct.idproduct !== line.idproduct;
          
          providerAssociations.push({
            idproduct: finalProductId,
            provider_code: line.barcode,
            description: line.description || null,
            is_ai_suggestion: isAiDetected,
            was_manually_confirmed: isAiDetected || wasManuallyMapped,
            line_index: index
          });

          // Preparar para bulk insert
          combinations.push({
            idproduct: finalProductId,
            code: line.barcode
          });
        }
        
        return {
          ...line,
          idproduct: finalProductId,
          product: mappedProduct || null,
          idposto: selectedPosto || line.idposto, // Aplicar posto selecionado (objeto completo)
          _ai_detected: line.idproduct && !line.provider_code_confirmed, // Flag para indicar se foi detectado pela IA
          _manually_mapped: mappedProduct !== null // Flag para indicar se foi mapeado manualmente
        };
      }),
      _productMappings: productMappings, // Enviar mapeamentos completos
      _selectedPosto: selectedPosto, // Enviar posto selecionado
      _providerAssociations: providerAssociations // Enviar associações para criar no backend
    };

    // Criar códigos de fornecedor em bulk se houver combinações
    if (combinations.length > 0 && parsedData.idprovider) {
      try {
        setIsCreatingCodes(true);
        
        const result = await productsService.bulkSetProviderCodes(
          profile.idEmpresa,
          parsedData.idprovider,
          combinations
        );
        
        if (result.success || result.success === 'partial') {
          console.log(`✅ Códigos criados: ${result.created}, Já existentes: ${result.exists}, Erros: ${result.errors}`);
          
          // Guardar resultado para mostrar feedback
          setCodesCreationResult({
            success: true,
            created: result.created,
            exists: result.exists,
            errors: result.errors,
            total: result.total
          });
          
          // Adicionar informação sobre os códigos ao updatedData
          updatedData._providerCodesResult = {
            created: result.created,
            exists: result.exists,
            errors: result.errors,
            total: result.total
          };

          // Aguardar um pouco para mostrar o feedback
          await new Promise(resolve => setTimeout(resolve, 1500));
        } else {
          console.error('Erro ao criar códigos:', result);
          setCodesCreationResult({
            success: false,
            message: 'Erro ao criar códigos de fornecedor'
          });
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      } catch (err) {
        console.error('Erro ao criar códigos de fornecedor:', err);
        setCodesCreationResult({
          success: false,
          message: 'Erro de conexão ao criar códigos'
        });
        await new Promise(resolve => setTimeout(resolve, 2000));
      } finally {
        setIsCreatingCodes(false);
        setCodesCreationResult(null);
      }
    }

    onDataParsed(updatedData);
    handleClose();
  };

  const handleClose = () => {
    setParsedData(null);
    setError(null);
    setSupplierNotFound(false);
    setSupplierData(null);
    setSelectedProvider(null);
    setProductsNotFound([]);
    setProductMappings({});
    setManuallyConfirmedProducts(new Set());
    setIsCreatingSupplier(false);
    setSelectedPosto(null);
    setIsCreatingCodes(false);
    setCodesCreationResult(null);
    onClose();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content pdf-upload-modal">
        <div className="modal-header">
          <h2><span role="img" aria-label="documento">📄</span> Importar Fatura de Compra</h2>
          <button className="modal-close" onClick={handleClose}>
            <MdClose size={24} />
          </button>
        </div>

        <div className="modal-body">
          {!parsedData ? (
            <>
                {isUploading ? (
                    <div className="ai-loading-container">
                        <div className="robot-analysis-scene">
                            {/* Documento */}
                            <div className="document-paper">
                                <div className="document-lines"></div>
                                <div className="document-scan-beam"></div>
                            </div>
                            
                            {/* Robô */}
                            <div className="robot">
                                <div className="robot-antenna">
                                    <div className="antenna-ball"></div>
                                </div>
                                <div className="robot-head">
                                    <div className="robot-eye robot-eye-left"></div>
                                    <div className="robot-eye robot-eye-right"></div>
                                </div>
                                <div className="robot-body">
                                    <div className="robot-light"></div>
                                </div>
                                <div className="robot-arm">
                                    <div className="magnifying-glass">
                                        <div className="glass-lens">
                                            <div className="lens-glare"></div>
                                        </div>
                                        <div className="glass-handle"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div className="ai-loading-text">
                            <span className="ai-text-robotic">INTELIGÊNCIA ARTIFICIAL</span>
                            <div className="ai-progress-bar">
                                <div className="progress-fill"></div>
                            </div>
                            <span className="ai-status">Extração de dados em progresso...</span>
                        </div>
                    </div>
                ) : (
                    <div className="upload-section">
                        <div 
                            className="upload-area"
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <MdCloudUpload size={64} className="upload-icon" />
                            <div className="upload-text">
                            {isUploading ? 'A processar fatura...' : 'Arraste o PDF aqui ou clique para selecionar'}
                            </div>
                            <div className="upload-subtext">
                            Máximo 10MB • PDF, JPG, PNG
                            </div>
                        </div>

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => handleFileUpload(e.target.files[0])}
                            style={{ display: 'none' }}
                        />

                        {error && (
                            <div className="error-message">
                            <MdWarning size={20} />
                            {error}
                            </div>
                        )}
                        </div>
                )}
            </>
            
          ) : (
            <div className="parsed-data-section">
              {/* Fornecedor detectado */}
              {!supplierNotFound && parsedData?.idprovider && parsedData?._metadata?.supplier && (
                <div className="supplier-found">
                  <div className="success-header">
                    <MdCheck size={24} />
                    <h3>Fornecedor Detectado</h3>
                  </div>
                  <div className="supplier-info">
                    <p><strong>Nome:</strong> {parsedData._metadata.supplier.name}</p>
                    <p><strong>NIF:</strong> {parsedData._metadata.supplier.vat_number}</p>
                    {parsedData._metadata.supplier.address && (
                      <p><strong>Morada:</strong> {parsedData._metadata.supplier.address}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Fornecedor não encontrado */}
              {supplierNotFound && (
                <div className="supplier-not-found">
                  <div className="warning-header">
                    <MdWarning size={24} />
                    <h3>Fornecedor não encontrado</h3>
                  </div>
                  <div className="supplier-info">
                    <p><strong>Nome:</strong> {supplierData?.name}</p>
                    <p><strong>NIF:</strong> {supplierData?.vat_number}</p>
                    <p><strong>Morada:</strong> {supplierData?.address}</p>
                  </div>
                  
                  {/* Opção 1: Selecionar fornecedor existente */}
                  <div className="provider-selection-section">
                    <h4 className="provider-selection-title">
                      <span role="img" aria-label="fornecedor">🏢</span> Selecionar Fornecedor Existente
                    </h4>
                    <p className="provider-selection-subtitle">
                      O NIF não foi encontrado. Selecione o fornecedor correto da lista ou crie um novo.
                    </p>
                    <AutoCompleteField
                      options={providers || []}
                      value={selectedProvider}
                      onChange={(_, provider) => setSelectedProvider(provider)}
                      getOptionLabel={(option) => {
                        if (!option) return "";
                        return option.name || option.label || "";
                      }}
                      label="Selecione o fornecedor"
                      searchableFields={['name', 'nif', 'label']}
                      noResultsText="Digite para filtrar fornecedores..."
                      className="pdf-modal-autocomplete"
                    />
                    <button 
                      className="btn btn-primary"
                      onClick={handleUseSelectedProvider}
                      disabled={!selectedProvider}
                      style={{ marginTop: '12px' }}
                    >
                      <MdCheck size={20} />
                      Usar Fornecedor Selecionado
                    </button>
                  </div>

                  {/* Divisor */}
                  <div className="provider-divider">
                    <span>OU</span>
                  </div>

                  {/* Opção 2: Criar novo fornecedor */}
                  <div className="provider-creation-section">
                    <h4 className="provider-creation-title">
                      <span role="img" aria-label="novo">✨</span> Criar Novo Fornecedor
                    </h4>
                    <button 
                      className="btn btn-primary"
                      onClick={handleCreateSupplier}
                      disabled={isCreatingSupplier}
                    >
                      {isCreatingSupplier ? (
                        <>
                          <div className="button-spinner"></div>
                          A criar fornecedor...
                        </>
                      ) : (
                        <>
                          <MdCheck size={20} />
                          Criar Novo Fornecedor
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Seleção de Posto */}
              <div className="posto-selection">
                <h4 className="posto-selection-title">
                  <span role="img" aria-label="localização">📍</span> Posto de Destino (Opcional)
                </h4>
                <p className="posto-selection-subtitle">
                  Selecione o posto para onde vão os produtos desta fatura.<br />
                  <small>
                  Poderá ser adicionado manualmente na fase de preenchimento dos produtos.
                  </small>
                </p>
                <AutoCompleteField
                  options={postos || []}
                  value={selectedPosto}
                  onChange={(_, posto) => setSelectedPosto(posto)}
                  getOptionLabel={(option) => {
                    if (!option) return "";
                    return option.label || option.nome || option.value || "";
                  }}
                  label="Selecione o posto"
                  searchableFields={['nome', 'label', 'value']}
                  noResultsText="Digite para filtrar postos..."
                  className="pdf-modal-autocomplete"
                />
              </div>

              {/* Resumo dos dados */}
              <div className="data-summary">
                <h3><span role="img" aria-label="sucesso">✅</span> Dados extraídos com sucesso</h3>
                <div className="summary-grid">
                  <div className="summary-item">
                    <label>Número do Documento:</label>
                    <span>{parsedData.doc || 'N/A'}</span>
                  </div>
                  <div className="summary-item">
                    <label>Data:</label>
                    <span>{parsedData.date_time ? new Date(parsedData.date_time * 1000).toLocaleDateString('pt-PT') : 'N/A'}</span>
                  </div>
                  <div className="summary-item">
                    <label>Linhas de Produtos:</label>
                    <span>{parsedData.lines?.length || 0}</span>
                  </div>
                </div>

                {/* Comparação de Totais */}
                {parsedData._metadata?.totals_comparison ? (
                  <div className="totals-comparison">
                    <h4><span role="img" aria-label="calculadora">🧮</span> Comparação de Totais</h4>
                    <div className="comparison-grid">
                      {/* Calculado das linhas */}
                      <div className="comparison-column">
                        <div className="comparison-header">
                          <span className="comparison-badge calculated">Calculado</span>
                          <small>Das linhas do documento</small>
                        </div>
                        <div className="totals-detail">
                          <div className="total-row total">
                            <span>Total:</span>
                            <strong>€{parsedData._metadata.totals_comparison.calculated_from_lines?.total?.toFixed(2) || '0.00'}</strong>
                          </div>
                        </div>
                      </div>

                      {/* Extraído pela IA */}
                      {parsedData._metadata.totals_comparison.extracted_by_gemini?.total > 0 && (<div className="comparison-column">
                        <div className="comparison-header">
                          <span className="comparison-badge extracted">
                            <MdAutoAwesome size={14} /> Extraído IA
                          </span>
                          <small>Do documento PDF</small>
                        </div>
                        <div className="totals-detail">
                          <div className="total-row total">
                            <span>Total:</span>
                            <strong>€{parsedData._metadata.totals_comparison.extracted_by_gemini?.total?.toFixed(2) || '0.00'}</strong>
                          </div>
                        </div>
                      </div>)}
                    </div>

                    {/* Aviso se houver diferenças */}
                    {(() => {
                      const calc = parsedData._metadata.totals_comparison.calculated_from_lines;
                      const extracted = parsedData._metadata.totals_comparison.extracted_by_gemini;
                      const hasDifference = calc && extracted && (
                        Math.abs(calc.total - extracted.total) > 0.01
                      );
                      
                      if (hasDifference) {
                        const diff = Math.abs(calc.total - extracted.total).toFixed(2);
                        return (
                          <div className="totals-warning">
                            <MdWarning size={16} />
                            <span>Diferença de €{diff} entre valores calculados e extraídos.</span>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                ) : (
                  /* Fallback para formato antigo */
                  <div className="summary-item" style={{ gridColumn: '1 / -1', marginTop: '12px' }}>
                    <label>Total:</label>
                    <span style={{ fontSize: '1.2em', color: '#2d3748', fontWeight: 'bold' }}>
                      €{parsedData._metadata?.totals?.total?.toFixed(2) || 'N/A'}
                    </span>
                  </div>
                )}
              </div>

              {/* Produtos já confirmados */}
              {parsedData.lines?.filter(line => line.match_confidence === "exact").length > 0 && (
                <div className="products-confirmed">
                  <div className="success-header">
                    <MdCheck size={24} />
                    <h3>Produtos Confirmados ({parsedData.lines.filter(line => line.match_confidence === "exact").length})</h3>
                  </div>
                  <p className="products-subtitle">
                    Produtos que já estão associados ao fornecedor
                  </p>
                  {/* Agrupar produtos confirmados por página */}
                  {Object.entries(
                    parsedData.lines
                      .filter(line => line.match_confidence === "exact")
                      .reduce((acc, product) => {
                        const page = product.page || 1;
                        if (!acc[page]) acc[page] = [];
                        acc[page].push(product);
                        return acc;
                      }, {})
                  ).sort(([pageA], [pageB]) => Number(pageA) - Number(pageB))
                    .map(([page, products]) => (
                      <div key={page} className="page-container">
                        <div className="page-header">
                          <span className="page-badge">Página {page}</span>
                          <span className="page-count">{products.length} {products.length === 1 ? 'produto' : 'produtos'}</span>
                        </div>
                        <div className="products-mapping-list">
                          {products.map((product, index) => (
                            <div key={index} className="product-mapping-item confirmed">
                              <div className="product-info">
                                <div className="product-header">
                                  <strong className="product-description">{product.description || 'Produto confirmado'}</strong>
                                  {product.barcode && (
                                    <span className="product-barcode">Código: {product.barcode}</span>
                                  )}
                                </div>
                                <div className="product-details-row">
                                  <div className="detail-col quantity-col">
                                    <span className="detail-label"><span role="img" aria-label="caixa">📦</span> Qtd</span>
                                    <span className="detail-value">{product.quantity}</span>
                                  </div>
                                  <div className="detail-col price-col">
                                    <span className="detail-label"><span role="img" aria-label="dinheiro">💰</span> s/IVA</span>
                                    <span className="detail-value">€{product.costprice_without_vat?.toFixed(3) || product.costprice?.toFixed(3) || product.unit_price_without_vat?.toFixed(3) || '0.000'}</span>
                                  </div>
                                  <div className="detail-col price-col">
                                    <span className="detail-label"><span role="img" aria-label="nota">💵</span> c/IVA</span>
                                    <span className="detail-value">€{product.costprice_with_vat?.toFixed(3) || (product.costprice * (1 + (product.iva || 0) / 100))?.toFixed(3) || product.unit_price_with_vat?.toFixed(3) || '0.000'}</span>
                                  </div>
                                  <div className="detail-col iva-col">
                                    <span className="detail-label"><span role="img" aria-label="gráfico">📊</span> IVA</span>
                                    <span className="detail-value">{product.iva}%</span>
                                  </div>
                                  {(product.desc > 0 || product.desc1 > 0 || product.desc2 > 0) && (
                                    <div className="detail-col discount-col">
                                      <span className="detail-label"><span role="img" aria-label="etiqueta">🏷️</span> Desc</span>
                                      <span className="detail-value">
                                        {product.desc > 0 && (<p>€{product.desc.toFixed(3)}</p>)}
                                        {product.desc1 > 0 && (<p>{product.desc1}%</p>)}
                                        {product.desc2 > 0 && (<p>{product.desc2}%</p>)}
                                      </span>
                                    </div>
                                  )}
                                  <div className="detail-col total-col">
                                    <span className="detail-label"><span role="img" aria-label="diamante">💎</span> Total</span>
                                    <span className="detail-value">€{((product.quantity || 0) * (product.unit_price_with_vat || product.costprice_with_vat || product.costprice || 0)).toFixed(3)}</span>
                                  </div>
                                </div>
                              </div>
                              <button 
                                className="delete-line-btn"
                                onClick={() => handleDeleteLine(product)}
                                title="Apagar linha"
                              >
                                <MdDelete size={18} />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                </div>
              )}

              {/* Produtos não encontrados */}
              {productsNotFound.length > 0 && (
                <div className="products-not-found">
                  <div className="warning-header">
                    <MdSearch size={24} />
                    <h3>Associar Produtos ({Object.keys(productMappings).filter(k => productMappings[k]).length}/{productsNotFound.length})</h3>
                  </div>
                  <p className="products-subtitle">
                    Produtos que precisam de associação manual
                  </p>
                  
                  {/* Legenda de cores */}
                  <div className="color-legend">
                    <div className="legend-item">
                      <span className="legend-icon ai-suggested-icon">?</span>
                      <span className="legend-text">Sugerido pela IA - Confirme ou altere</span>
                    </div>
                    <div className="legend-item">
                      <span className="legend-icon confirmed-icon">✓</span>
                      <span className="legend-text">Confirmado manualmente</span>
                    </div>
                    <div className="legend-item">
                      <span className="legend-icon pending-icon">○</span>
                      <span className="legend-text">Aguarda associação</span>
                    </div>
                  </div>
                  
                  {/* Agrupar produtos não encontrados por página */}
                  {Object.entries(
                    productsNotFound.reduce((acc, product) => {
                      const page = product.page || 1;
                      if (!acc[page]) acc[page] = [];
                      acc[page].push(product);
                      return acc;
                    }, {})
                  ).sort(([pageA], [pageB]) => Number(pageA) - Number(pageB))
                    .map(([page, pageProducts]) => (
                      <div key={page} className="page-container">
                        <div className="page-header">
                          <span className="page-badge">Página {page}</span>
                          <span className="page-count">{pageProducts.length} {pageProducts.length === 1 ? 'produto' : 'produtos'}</span>
                        </div>
                        <div className="products-mapping-list">
                          {pageProducts.map((product, lineIndex) => {
                            // Encontrar o índice real da linha no array completo
                            const actualLineIndex = parsedData.lines.findIndex(l => l === product);
                            const isAiDetected = product.idproduct && product.match_confidence === "match";
                            
                            // Verificar se o produto já está mapeado
                            const isMapped = productMappings[actualLineIndex] !== undefined && productMappings[actualLineIndex] !== null;
                            const isManuallyConfirmed = manuallyConfirmedProducts.has(actualLineIndex);
                            
                            // Determinar a classe CSS apropriada
                            let cardClass = 'product-mapping-item';
                            if (isMapped) {
                              if (isManuallyConfirmed) {
                                // Produto confirmado ou alterado manualmente pelo utilizador
                                cardClass += ' mapped';
                              } else if (isAiDetected) {
                                // Produto sugerido pela IA mas ainda não confirmado
                                cardClass += ' ai-suggested';
                              } else {
                                // Produto mapeado (não veio da IA)
                                cardClass += ' mapped';
                              }
                            }
                            
                            return (
                              <div key={actualLineIndex} className={cardClass}>
                                <div className="product-info">
                                  <div className="product-header">
                                    <strong className="product-description">{product.description || 'Sem descrição'}</strong>
                                    {isAiDetected && (
                                      <span className="ai-detected-badge" title="Produto detectado pela IA - Confirme ou altere a associação">
                                        <MdAutoAwesome size={14} /> IA Sugestão
                                      </span>
                                    )}
                                    {product.barcode && (
                                      <span className="product-barcode">Código: {product.barcode}</span>
                                    )}
                                  </div>
                                  <div className="product-details-row">
                                    <div className="detail-col quantity-col">
                                      <span className="detail-label"><span role="img" aria-label="caixa">📦</span> Qtd</span>
                                      <span className="detail-value">{product.quantity}</span>
                                    </div>
                                    <div className="detail-col price-col">
                                      <span className="detail-label"><span role="img" aria-label="dinheiro">💰</span> s/IVA</span>
                                      <span className="detail-value">€{product.costprice_without_vat?.toFixed(3) || product.unit_price_without_vat?.toFixed(3) || product.costprice?.toFixed(3) || '0.000'}</span>
                                    </div>
                                    <div className="detail-col price-col">
                                      <span className="detail-label"><span role="img" aria-label="nota">💵</span> c/IVA</span>
                                      <span className="detail-value">€{product.costprice_with_vat?.toFixed(3) || product.unit_price_with_vat?.toFixed(3) || (product.costprice * (1 + (product.iva || 0) / 100))?.toFixed(3) || '0.000'}</span>
                                    </div>
                                    <div className="detail-col iva-col">
                                      <span className="detail-label"><span role="img" aria-label="gráfico">📊</span> IVA</span>
                                      <span className="detail-value">{product.iva}%</span>
                                    </div>
                                    {(product.desc > 0 || product.desc1 > 0 || product.desc2 > 0) && (
                                      <div className="detail-col discount-col">
                                        <span className="detail-label"><span role="img" aria-label="etiqueta">🏷️</span> Desc</span>
                                        <span className="detail-value">
                                          {product.desc > 0 && (<p>€{product.desc.toFixed(3)}</p>)}
                                          {product.desc1 > 0 && (<p>{product.desc1}%</p>)}
                                          {product.desc2 > 0 && (<p>{product.desc2}%</p>)}
                                        </span>
                                      </div>
                                    )}
                                    <div className="detail-col total-col">
                                      <span className="detail-label"><span role="img" aria-label="diamante">💎</span> Total</span>
                                      <span className="detail-value">€{((product.quantity || 0) * (product.unit_price_with_vat || product.costprice_with_vat || product.costprice || 0)).toFixed(3)}</span>
                                    </div>
                                  </div>
                                </div>
                                <div className="product-selector">
                                  <div className="product-selector-row">
                                    <AutoCompleteField
                                      options={products || []}
                                      value={productMappings[actualLineIndex] || null}
                                      onChange={(_, selectedProduct) => {
                                        handleProductMapping(actualLineIndex, selectedProduct);
                                      }}
                                      getOptionLabel={(option) => {
                                        if (!option) return "";
                                        return option.label || option.nome || option.idproduct || "";
                                      }}
                                      label="Selecione o produto"
                                      searchableFields={['nome', 'label', 'idproduct']}
                                      noResultsText="Digite para filtrar produtos..."
                                      className="pdf-modal-autocomplete"
                                      noResultsAction={{
                                        text: "Criar novo produto",
                                        action: (inputValue) => {
                                          alert(`Funcionalidade para criar produto "${inputValue}" será implementada`);
                                        }
                                      }}
                                    />
                                    {/* Botão de confirmação para sugestões da IA */}
                                    {isAiDetected && isMapped && !isManuallyConfirmed && (
                                      <button 
                                        className="confirm-ai-btn"
                                        onClick={() => {
                                          setManuallyConfirmedProducts(prev => {
                                            const newSet = new Set(prev);
                                            newSet.add(actualLineIndex);
                                            return newSet;
                                          });
                                        }}
                                        title="Confirmar sugestão da IA"
                                      >
                                        <MdCheck size={20} />
                                      </button>
                                    )}
                                  </div>
                                  {(!products || products.length === 0) && (
                                    <p style={{ fontSize: '12px', color: '#e53e3e', marginTop: '4px' }}>
                                      <span role="img" aria-label="aviso">⚠️</span> Nenhum produto disponível. Recarregue a página.
                                    </p>
                                  )}
                                </div>
                                <button 
                                  className="delete-line-btn"
                                  onClick={() => handleDeleteLine(product)}
                                  title="Apagar linha"
                                >
                                  <MdDelete size={18} />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                </div>
              )}

              <div className="modal-actions">
                {productsNotFound.length > 0 && !areAllProductsMapped() && !isCreatingCodes && (
                  <div className="validation-message">
                    <MdWarning size={16} />
                    <span>Associe todos os produtos antes de continuar</span>
                  </div>
                )}
                
                {isCreatingCodes && (
                  <div className="validation-message" style={{ background: '#ebf8ff', borderColor: '#90cdf4', color: '#2c5282' }}>
                    <div className="button-spinner" style={{ borderTopColor: '#2c5282', borderColor: 'rgba(44, 82, 130, 0.3)' }}></div>
                    <span>A criar códigos de fornecedor...</span>
                  </div>
                )}

                {codesCreationResult && codesCreationResult.success && (
                  <div className="validation-message" style={{ background: '#f0fff4', borderColor: '#9ae6b4', color: '#22543d' }}>
                    <MdCheck size={16} />
                    <span>
                      {codesCreationResult.created} {codesCreationResult.created === 1 ? 'código criado' : 'códigos criados'}
                      {codesCreationResult.exists > 0 && `, ${codesCreationResult.exists} já ${codesCreationResult.exists === 1 ? 'existia' : 'existiam'}`}
                    </span>
                  </div>
                )}

                <button 
                  className="btn btn-secondary" 
                  onClick={handleClose}
                  disabled={isCreatingCodes}
                >
                  Cancelar
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={handleConfirmData}
                  disabled={supplierNotFound || !areAllProductsMapped() || isCreatingCodes}
                  title={!areAllProductsMapped() ? 'Associe todos os produtos primeiro' : ''}
                >
                  {isCreatingCodes ? (
                    <>
                      <div className="button-spinner"></div>
                      A processar...
                    </>
                  ) : (
                    <>
                      <MdCheck size={20} />
                      Confirmar e Preencher
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PDFUploadModal;
